import type { CrremSettings } from "features/energy-analysis-dashboard/model/types";

import {
  DEFAULT_CRREM_SETTINGS,
  isCrremDisplayMetric,
  isCrremTrendMethod,
} from "features/energy-analysis-dashboard/model/constants";
import { LocalStorageService } from "utils/localStorage";

import type { CrremPathwayStoredConfig } from "../model/types";

import { CRREM_PATHWAY_PARTITION_BY, CRREM_PATHWAY_SCALE, DEFAULT_CRREM_PATHWAY_CONFIG } from "../model/constants";

type ConfigListener = (config: CrremPathwayStoredConfig) => void;

const cache = new Map<string, CrremPathwayStoredConfig>();
const listeners = new Map<string, Set<ConfigListener>>();

const storageKey = (projectId: string) => `crremPathwaySettings:${projectId}`;
const legacyStorageKey = (projectId: string) => `crremSettings:${projectId}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isPartitionBy = (value: unknown): value is CrremPathwayStoredConfig["partitionBy"] =>
  value === CRREM_PATHWAY_PARTITION_BY.none ||
  value === CRREM_PATHWAY_PARTITION_BY.useType ||
  value === CRREM_PATHWAY_PARTITION_BY.utility;

const isScale = (value: unknown): value is CrremPathwayStoredConfig["scale"] =>
  value === CRREM_PATHWAY_SCALE.grossArea ||
  value === CRREM_PATHWAY_SCALE.netLettableArea ||
  value === CRREM_PATHWAY_SCALE.none;

const parseVisibleCorrectionIds = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];

export const parseCrremPathwayConfig = (payload: unknown): CrremPathwayStoredConfig => {
  const source = isRecord(payload) ? payload : {};
  return {
    ...DEFAULT_CRREM_PATHWAY_CONFIG,
    displayMetric: isCrremDisplayMetric(source.displayMetric)
      ? source.displayMetric
      : DEFAULT_CRREM_PATHWAY_CONFIG.displayMetric,
    partitionBy: isPartitionBy(source.partitionBy) ? source.partitionBy : DEFAULT_CRREM_PATHWAY_CONFIG.partitionBy,
    scale: isScale(source.scale) ? source.scale : DEFAULT_CRREM_PATHWAY_CONFIG.scale,
    scenarioKey:
      typeof source.scenarioKey === "string" && source.scenarioKey.length > 0 ? source.scenarioKey : undefined,
    trendMethod: isCrremTrendMethod(source.trendMethod) ? source.trendMethod : DEFAULT_CRREM_PATHWAY_CONFIG.trendMethod,
    visibleCorrectionIds: parseVisibleCorrectionIds(source.visibleCorrectionIds),
  };
};

export const readCrremPathwayConfig = (projectId: string) => {
  const cached = cache.get(projectId);
  if (cached) return cached;
  const stored = LocalStorageService.getItem(storageKey(projectId));
  const loaded = parseCrremPathwayConfig(stored ?? LocalStorageService.getItem(legacyStorageKey(projectId)));
  cache.set(projectId, loaded);
  return loaded;
};

const configEquals = (left: CrremPathwayStoredConfig, right: CrremPathwayStoredConfig) =>
  left.displayMetric === right.displayMetric &&
  left.partitionBy === right.partitionBy &&
  left.scale === right.scale &&
  left.scenarioKey === right.scenarioKey &&
  left.trendMethod === right.trendMethod &&
  left.visibleCorrectionIds.length === right.visibleCorrectionIds.length &&
  left.visibleCorrectionIds.every((id, index) => id === right.visibleCorrectionIds[index]);

export const writeCrremPathwayConfig = (projectId: string, next: CrremPathwayStoredConfig) => {
  const parsed = parseCrremPathwayConfig(next);
  const previous = cache.get(projectId) ?? readCrremPathwayConfig(projectId);
  if (configEquals(previous, parsed)) {
    return previous;
  }
  cache.set(projectId, parsed);
  LocalStorageService.setItem(storageKey(projectId), parsed);
  listeners.get(projectId)?.forEach((listener) => listener(parsed));
  return parsed;
};

export const patchCrremPathwayConfig = (projectId: string, change: Partial<CrremPathwayStoredConfig>) =>
  writeCrremPathwayConfig(projectId, { ...readCrremPathwayConfig(projectId), ...change });

export const subscribeCrremPathwayConfig = (projectId: string, listener: ConfigListener) => {
  const group = listeners.get(projectId) ?? new Set<ConfigListener>();
  group.add(listener);
  listeners.set(projectId, group);
  return () => {
    group.delete(listener);
    if (group.size === 0) listeners.delete(projectId);
  };
};

export const toPathwayCrremSettings = (config: CrremPathwayStoredConfig): CrremSettings => ({
  ...DEFAULT_CRREM_SETTINGS,
  displayMetric: config.displayMetric,
  trendMethod: config.trendMethod,
});
